import React, { useEffect, useState } from "react";
import {
    BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
    PieChart, Pie, Cell, RadialBarChart, RadialBar, AreaChart, Area, Legend,
} from "recharts";
import {
    ShieldAlert, Layers, FileSearch, Lightbulb, CheckCircle2,
    AlertTriangle, ShieldCheck, Activity, ArrowUpRight, Workflow, Flame, Skull,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel, StatTile } from "@/components/common/Panel";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { SkeletonStat } from "@/components/common/Skeleton";
import { SeverityBadge } from "@/components/common/Badges";
import PasteTextPanel from "@/components/common/PasteTextPanel";
import { GenesisHero } from "@/components/dashboard/GenesisHero";
import { ModuleLaunchpad } from "@/components/dashboard/ModuleLaunchpad";
import { TID } from "@/lib/testIds";

const SEV_COLORS = {
    critical: "var(--neon-red)",
    high: "#FF6B5A",
    medium: "var(--warning)",
    low: "#FFD166",
    info: "var(--neon-cyan)",
};
const ENGINE_COLORS = {
    authorization: "var(--neon-cyan)",
    su24: "var(--neon-blue)",
    odata: "var(--metal)",
    correlation: "var(--warning)",
};

/** Semantic gauge colour so a healthy score never reads like an alarm.
 * `inverted` flips the scale for metrics where high is bad (Risk). */
const gaugeTone = (v, inverted = false) => {
    const good = inverted ? v <= 30 : v >= 75;
    const mid = inverted ? v <= 60 : v >= 45;
    return good ? "var(--safe)" : mid ? "var(--warning)" : "var(--neon-red)";
};

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState([]);
    const [recentFindings, setRecentFindings] = useState([]);
    const [workspaces, setWorkspaces] = useState([]);
    const [heatmap, setHeatmap] = useState(null);
    const [roleStats, setRoleStats] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [a, b, c, d, e] = await Promise.all([
                    api.get("/stats/overview"),
                    api.get("/stats/recent-activity"),
                    api.get("/findings"),
                    api.get("/workspaces"),
                    api.get("/roles-stats/heatmap"),
                ]);
                setStats(a.data);
                setActivity(b.data);
                setRecentFindings(c.data.slice(0, 8));
                setWorkspaces(d.data.slice(0, 6));
                setHeatmap(e.data);
            } catch (e) {
                console.warn("Dashboard data fetch failed", e);
            }
        })();
        // Separate, independently-caught call: roles-stats is gated behind the
        // Role Engineering module, so it must not break the rest of the
        // dashboard for users without that module.
        (async () => {
            try {
                const { data } = await api.get("/roles-stats/overview");
                setRoleStats(data);
            } catch (e) {
                console.warn("Role verification stats unavailable for this user", e);
            }
        })();
    }, []);

    const sev = stats?.severity_counts || {};
    const severityData = ["critical", "high", "medium", "low", "info"].map((k) => ({
        name: k.toUpperCase(),
        value: sev[k] || 0,
        fill: SEV_COLORS[k],
    }));
    const engineCounts = stats?.engine_counts || {};
    const engineData = Object.entries(engineCounts).map(([k, v]) => ({
        name: k,
        value: v,
        fill: ENGINE_COLORS[k] || "var(--neon-cyan)",
    }));
    const riskScore = stats?.avg_risk_score ?? 0;
    const confidence = stats?.avg_confidence_score ?? 0;
    const cleanCore = stats?.avg_clean_core_score ?? 0;

    // Per-workspace posture comparison. Real workspace names, never synthetic
    // series: a single workspace gets a readout instead of a meaningless trend.
    const trendData = workspaces.slice(0, 8).map((w) => ({
        t: w.name.length > 18 ? `${w.name.slice(0, 17)}…` : w.name,
        fullName: w.name,
        risk: w.risk_score || 0,
        confidence: w.confidence_score || 0,
        clean: w.clean_core_score || 0,
    }));

    // SoD rules are identified by ID in SAP GRC practice; enriched rules carry a
    // full sentence as their name, which is unreadable on a chart axis.
    const heatmapRules = (heatmap?.top_rules || []).map((r) => ({
        ...r,
        label: `${r.rule_id} · ${r.rule_name.length > 20 ? `${r.rule_name.slice(0, 19)}…` : r.rule_name}`,
    }));

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <GenesisHero stats={stats} roleStats={roleStats} />

            <ModuleLaunchpad />

            {/* KPI tiles — click through to the underlying data source */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {!stats
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonStat key={i} testId={`stat-skeleton-${i}`} />)
                    : [
                    { k: "workspaces", label: "Workspaces", icon: Layers, value: stats?.workspaces ?? "—", sub: "active engineering projects", tone: "cyan", to: "/workspaces" },
                    { k: "artifacts", label: "Artifacts Parsed", icon: FileSearch, value: stats?.artifacts ?? "—", sub: "SAP exports analyzed", tone: "slate", to: "/workspaces" },
                    { k: "findings", label: "Engineering Findings", icon: ShieldAlert, value: stats?.findings ?? "—", sub: "detected across engines", tone: "amber", to: "/findings" },
                    { k: "open-recommendations", label: "Open Recommendations", icon: Lightbulb, value: stats?.open_recommendations ?? "—", sub: "awaiting architect review", tone: "green", to: "/recommendations" },
                ].map((s) => (
                    <div
                        key={s.k}
                        data-testid={`dashboard-kpi-link-${s.k}`}
                        onClick={() => nav(s.to)}
                        onKeyDown={(e) => { if (e.key === "Enter") nav(s.to); }}
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer"
                        title={`Open ${s.label}`}
                    >
                        <StatTile testId={TID.statTile(s.k)} label={s.label} value={s.value} sub={s.sub} icon={s.icon} tone={s.tone} />
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Engineering Posture Radials */}
                <Panel
                    className="lg:col-span-5"
                    eyebrow="Engineering Posture"
                    title="Composite Risk · Confidence · Clean Core"
                    testId={TID.riskScoreGauge}
                >
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: "Risk", value: riskScore, color: gaugeTone(riskScore, true), to: "/risk", hint: "Open the Risk Dashboard" },
                            { label: "Confidence", value: confidence, color: gaugeTone(confidence), to: "/findings", hint: "Open Findings (confidence source)" },
                            { label: "Clean Core", value: cleanCore, color: gaugeTone(cleanCore), to: "/clean-core", hint: "Open the Clean Core module" },
                        ].map((g) => (
                            <div
                                key={g.label}
                                data-testid={`dashboard-gauge-${g.label.toLowerCase().replace(" ", "-")}`}
                                onClick={() => nav(g.to)}
                                onKeyDown={(e) => { if (e.key === "Enter") nav(g.to); }}
                                role="button"
                                tabIndex={0}
                                title={g.hint}
                                className="flex flex-col items-center cursor-pointer rounded-sm hover:bg-white/[0.03] transition-colors py-1"
                            >
                                <div className="w-32 h-32 relative">
                                    <ResponsiveContainer width={128} height={128}>
                                        <RadialBarChart
                                            innerRadius="70%"
                                            outerRadius="100%"
                                            data={[{ name: g.label, value: g.value, fill: g.color }]}
                                            startAngle={210}
                                            endAngle={-30}
                                        >
                                            <RadialBar background={{ fill: "rgba(148,163,184,0.08)" }} dataKey="value" cornerRadius={2} />
                                        </RadialBarChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <div className="font-display text-2xl text-white">
                                            <AnimatedNumber value={g.value} />
                                        </div>
                                        <div className="font-mono text-[10px] text-slate-500">/100</div>
                                    </div>
                                </div>
                                <div className="mt-2 label-eyebrow" style={{ color: g.color }}>
                                    {g.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>

                {/* Severity distribution */}
                <Panel className="lg:col-span-4" eyebrow="Distribution" title="Findings by Severity" testId={TID.severityChart}>
                    <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={220}>
                        <BarChart data={severityData} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
                            <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                            <YAxis stroke="#94a3b8" allowDecimals={false} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                            <Tooltip
                                cursor={{ fill: "rgb(var(--accent-1-rgb) / 0.06)" }}
                                contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--accent-1-rgb) / 0.25)", borderRadius: 2, fontSize: 12 }}
                            />
                            <Bar
                                dataKey="value"
                                radius={[2, 2, 0, 0]}
                                cursor="pointer"
                                onClick={(d) => d?.name && nav(`/findings?severity=${d.name.toLowerCase()}`)}
                            >
                                {severityData.map((d) => (
                                    <Cell key={d.name} fill={d.fill} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Panel>

                {/* Engine pie */}
                <Panel className="lg:col-span-3" eyebrow="Engines" title="Detection Source" testId={TID.engineChart}>
                    <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={220}>
                        <PieChart>
                            <Pie
                                data={engineData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={45}
                                outerRadius={75}
                                paddingAngle={2}
                                cursor="pointer"
                                onClick={(d) => d?.name && nav(`/findings?engine=${d.name}`)}
                            >
                                {engineData.map((d) => (
                                    <Cell key={d.name} fill={d.fill} stroke="rgba(0,0,0,0.4)" />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--accent-1-rgb) / 0.25)", borderRadius: 2, fontSize: 12 }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-2 mt-3 justify-center">
                        {engineData.map((d) => (
                            <button
                                key={d.name}
                                data-testid={`dashboard-engine-legend-${d.name}`}
                                onClick={() => nav(`/findings?engine=${d.name}`)}
                                className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-cyan-300 transition-colors"
                                title={`View ${d.name} findings`}
                            >
                                <span className="dot" style={{ background: d.fill }} />
                                {d.name}
                            </button>
                        ))}
                    </div>
                </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Trend */}
                <Panel className="lg:col-span-8" eyebrow="Workspace Telemetry" title="Risk vs Confidence vs Clean Core" testId="dashboard-telemetry-panel">
                    {trendData.length < 2 ? (
                        <div className="py-6" data-testid="dashboard-telemetry-single">
                            {trendData.length === 1 ? (
                                <>
                                    <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{trendData[0].fullName}</div>
                                    <div className="grid grid-cols-3 gap-3 mt-4">
                                        {[
                                            { l: "Risk", v: trendData[0].risk, c: gaugeTone(trendData[0].risk, true) },
                                            { l: "Confidence", v: trendData[0].confidence, c: gaugeTone(trendData[0].confidence) },
                                            { l: "Clean Core", v: trendData[0].clean, c: gaugeTone(trendData[0].clean) },
                                        ].map((m) => (
                                            <div key={m.l} className="panel px-4 py-4">
                                                <div className="font-display text-3xl" style={{ color: m.c }}>{m.v.toFixed(1)}</div>
                                                <div className="label-eyebrow mt-1.5">{m.l}</div>
                                                <div className="h-1 mt-3 rounded-full bg-white/8 overflow-hidden">
                                                    <div className="h-full rounded-full transition-[width] duration-1000" style={{ width: `${m.v}%`, background: m.c }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-4">
                                        Create a second workspace to plot posture side by side and watch these three metrics diverge.
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-slate-500 text-center py-8">
                                    No workspaces yet — create one and run an analysis to start plotting posture.
                                </p>
                            )}
                        </div>
                    ) : (
                    <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={260}>
                        <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -16, bottom: 0 }}>
                            <defs>
                                <linearGradient id="riskG" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="var(--neon-red)" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="var(--neon-red)" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="confG" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="var(--safe)" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="var(--safe)" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="ccG" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="var(--warning)" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="var(--warning)" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.07)" />
                            <XAxis dataKey="t" stroke="#94a3b8" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                            <YAxis stroke="#94a3b8" allowDecimals={false} domain={[0, 100]} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                            <Tooltip
                                contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--accent-1-rgb) / 0.25)", borderRadius: 2, fontSize: 12 }}
                                labelFormatter={(_, p) => p?.[0]?.payload?.fullName || ""}
                            />
                            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono", textTransform: "uppercase" }} />
                            <Area type="monotone" dataKey="risk" name="Risk" stroke="var(--neon-red)" fill="url(#riskG)" strokeWidth={1.5} />
                            <Area type="monotone" dataKey="confidence" name="Confidence" stroke="var(--safe)" fill="url(#confG)" strokeWidth={1.5} />
                            <Area type="monotone" dataKey="clean" name="Clean Core" stroke="var(--warning)" fill="url(#ccG)" strokeWidth={1.5} />
                        </AreaChart>
                    </ResponsiveContainer>
                    )}
                </Panel>

                {/* Recent activity */}
                <Panel className="lg:col-span-4" eyebrow="Audit Trail" title="Recent Activity" testId={TID.recentActivity}>
                    <ul className="space-y-3 max-h-[260px] overflow-y-auto">
                        {activity.length === 0 && <li className="text-xs text-slate-500 font-mono">No activity yet.</li>}
                        {activity.map((a) => (
                            <li key={a.id} className="flex items-start gap-3 text-[12px]">
                                <span className="dot mt-2" style={{ background: "var(--neon-cyan)", boxShadow: "0 0 8px var(--neon-cyan)" }} />
                                <div className="min-w-0">
                                    <div className="text-slate-200 truncate">
                                        <span className="font-mono text-cyan-300 text-[11px] mr-1">{a.action}</span>
                                        <span className="text-slate-500">·</span>
                                        <span className="text-slate-400 ml-1">{a.entity_type}</span>
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                                        {a.actor_email} · {new Date(a.created_at).toLocaleString()}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <Panel
                    className="lg:col-span-7"
                    eyebrow="Findings"
                    title="Latest Engineering Findings"
                    action={
                        <button onClick={() => nav("/findings")} className="btn-ghost flex items-center gap-1">
                            View all <ArrowUpRight size={12} />
                        </button>
                    }
                >
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th>Severity</th>
                                <th>Title</th>
                                <th>Engine</th>
                                <th>Confidence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentFindings.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center text-xs text-slate-500 font-mono py-6">
                                        Run an analysis on a workspace to populate findings.
                                    </td>
                                </tr>
                            )}
                            {recentFindings.map((f) => (
                                <tr
                                    key={f.id}
                                    data-testid={`dashboard-finding-row-${f.id}`}
                                    onClick={() => nav(`/findings?severity=${f.severity}&engine=${f.engine}`)}
                                    className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                                    title="Drill into Findings filtered to this severity & engine"
                                >
                                    <td><SeverityBadge severity={f.severity} /></td>
                                    <td className="max-w-[280px] truncate">{f.title}</td>
                                    <td className="font-mono text-[11px] uppercase text-cyan-300/80">{f.engine}</td>
                                    <td className="font-mono text-cyan-300">{Math.round((f.confidence || 0) * 100)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Panel>

                <Panel
                    className="lg:col-span-5"
                    eyebrow="Quick Access"
                    title="Engineering Workspaces"
                    action={
                        <button onClick={() => nav("/workspaces")} className="btn-ghost flex items-center gap-1">
                            All workspaces <ArrowUpRight size={12} />
                        </button>
                    }
                >
                    <div className="space-y-2">
                        {workspaces.length === 0 && (
                            <div className="text-xs text-slate-500 font-mono">No workspaces yet.</div>
                        )}
                        {workspaces.map((w) => (
                            <button
                                key={w.id}
                                onClick={() => nav(`/workspaces/${w.id}`)}
                                className="w-full text-left panel hover:border-cyan-500/40 transition-colors px-4 py-3 flex items-center justify-between group"
                            >
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate">{w.name}</div>
                                    <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                                        {w.artifact_count} artifacts · {w.finding_count} findings
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <div className="text-right">
                                        <div className="font-display text-lg text-rose-400">{w.risk_score?.toFixed?.(0) ?? 0}</div>
                                        <div className="text-[9px] font-mono uppercase text-slate-500">risk</div>
                                    </div>
                                    <ArrowUpRight size={14} className="text-slate-600 group-hover:text-cyan-400 transition-colors" />
                                </div>
                            </button>
                        ))}
                    </div>
                </Panel>
            </div>

            {/* SoD Conflict Heatmap + Top Toxic Roles — aggregated across the entire role library */}
            {heatmap && (heatmap.top_rules.length > 0 || heatmap.top_roles.length > 0) && (
                <div className="grid lg:grid-cols-5 gap-4">
                    <Panel
                        eyebrow={`${heatmap.roles_scanned} roles scanned`}
                        title={<span className="inline-flex items-center gap-1.5"><Flame size={14} className="text-rose-400" /> SoD Conflict Heatmap</span>}
                        className="lg:col-span-3"
                        action={<button data-testid={TID.dashboardHeatmapRolesLink} onClick={() => nav("/roles")} className="text-[10px] font-mono text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">Open Role Engineering <ArrowUpRight size={11} /></button>}
                    >
                        {heatmap.top_rules.length === 0 ? (
                            <p className="text-sm text-slate-500 font-mono py-6 text-center">No SoD conflicts across the role library.</p>
                        ) : (
                            <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={Math.max(220, heatmap.top_rules.length * 32)}>
                                <BarChart data={heatmapRules} layout="vertical" margin={{ left: 8, right: 24 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fill: "#64748B", fontSize: 10 }} />
                                    <YAxis
                                        type="category" dataKey="label" width={180}
                                        tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "monospace" }}
                                    />
                                    <Tooltip
                                        contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--accent-1-rgb) / 0.2)", borderRadius: 4, fontSize: 11 }}
                                        labelStyle={{ color: "#fff" }}
                                        labelFormatter={(_, p) => p?.[0]?.payload?.rule_name || ""}
                                        formatter={(v, _n, p) => [`${v} role(s) affected`, p?.payload?.rule_id]}
                                    />
                                    <Bar
                                        dataKey="count"
                                        radius={[0, 3, 3, 0]}
                                        cursor="pointer"
                                        onClick={(d) => d?.rule_id && nav(`/roles?rule=${d.rule_id}`)}
                                    >
                                        {heatmapRules.map((r, i) => (
                                            <Cell key={i} fill={SEV_COLORS[r.severity] || SEV_COLORS.medium} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Panel>

                    <Panel eyebrow="Toxicity ranked" title={<span className="inline-flex items-center gap-1.5"><Skull size={14} className="text-orange-400" /> Top Toxic Roles</span>} className="lg:col-span-2">
                        {heatmap.top_roles.length === 0 ? (
                            <p className="text-sm text-slate-500 font-mono py-6 text-center">Role library is clean.</p>
                        ) : (
                            <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
                                {heatmap.top_roles.map((r, i) => (
                                    <button
                                        key={r.role_id}
                                        data-testid={TID.dashboardToxicRoleRow(r.role_id)}
                                        onClick={() => nav(`/roles?expand=${r.role_id}`)}
                                        title="Open Sentinel Forge with this role expanded to its SoD detail"
                                        className="w-full flex items-center gap-2.5 panel px-2.5 py-2 rounded-[4px] hover:border-rose-500/30 transition-colors text-left"
                                    >
                                        <span className="text-[10px] font-mono text-slate-400 w-4 shrink-0">#{i + 1}</span>
                                        <span className="text-xs text-white font-mono truncate flex-1">{r.role_name}</span>
                                        <span className="text-[10px] font-mono text-rose-300 shrink-0">{r.conflict_count} SoD</span>
                                        {r.critical_count > 0 && <span className="text-[10px] font-mono text-orange-300 shrink-0">{r.critical_count} crit</span>}
                                        <SeverityBadge severity={r.risk_level} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </Panel>
                </div>
            )}

            {/* Verification Coverage KPI — % of role library that is field-value verified vs. generic object-name matches */}
            {roleStats && roleStats.total_roles > 0 && (
                <Panel
                    eyebrow="Role Library Data Quality"
                    title={<span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-cyan-400" /> Verification Coverage</span>}
                    action={<button data-testid={TID.dashboardHeatmapRolesLink} onClick={() => nav("/roles")} className="text-[10px] font-mono text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">Open Sentinel Forge <ArrowUpRight size={11} /></button>}
                >
                    <div className="grid md:grid-cols-[220px_1fr] gap-6 items-center" data-testid={TID.verificationCoverageKpi}>
                        <div className="flex flex-col items-center">
                            <ResponsiveContainer width={140} height={140}>
                                <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: roleStats.verification_coverage_pct }]} startAngle={90} endAngle={-270}>
                                    <RadialBar dataKey="value" cornerRadius={8} fill={roleStats.verification_coverage_pct >= 60 ? "#00E39A" : roleStats.verification_coverage_pct >= 30 ? "#FFB020" : "var(--neon-red)"} background={{ fill: "rgba(255,255,255,0.06)" }} max={100} />
                                </RadialBarChart>
                            </ResponsiveContainer>
                            <div className="-mt-[86px] text-center pointer-events-none">
                                <div className="font-display text-3xl text-white"><AnimatedNumber value={roleStats.verification_coverage_pct} decimals={1} />%</div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-slate-300 leading-relaxed">
                                <span className="text-cyan-300 font-mono">{roleStats.field_verified_roles}</span> of <span className="text-white font-mono">{roleStats.total_roles}</span> roles
                                in the library have been <strong className="text-white">field-value verified</strong> (explicit ACTVT/org-level values captured via the Bulk Field Editor),
                                rather than relying on generic object-name matches alone.
                            </p>
                            <p className="text-xs text-slate-500">
                                Field-verified roles produce far more precise SoD conflict detection and cleaner <code className="font-mono text-slate-400">.sap</code> exports.
                                Use the Bulk Field Editor in Sentinel Forge to raise this coverage.
                            </p>
                        </div>
                    </div>
                </Panel>
            )}

            {/* Quick analyze (ephemeral, role-gated) */}
            <PasteTextPanel workspaceId={null} compact />
        </div>
    );
}
